/* eslint-disable max-len */
import { Card, Col, Row } from 'react-bootstrap';
import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-google-charts';
import DataTable, { TableColumn } from 'react-data-table-component';

import { eachDayOfInterval, format, subDays } from 'date-fns';

import useSWQuery from 'shared-components/hooks/useSWQuery';
import { Method, Service } from 'shared-components/configuration';
import { useMerchantInfo } from 'shared-components/providers/MerchantInfoProvider';
import { GET_SEB_REPORT } from 'shared-components/queries/graphql';
import { SEB_REPORT_EMAIL } from 'shared-components/queries/mutations';

import LiveOperations from './widgets/liveOperations';
import { CorpoStats } from './widgets/corpoStats';

/*
import AccountSummary from '../../../components/AccountSummary';
import { MerchantTransaction } from '../../../types/grapql';
import { TRANSACTIONS_QUERY } from 'shared-components/queries/graphql';
import { useBalances } from '../../../contexts/balances';
import useRfqs from '../../../hooks/useRfqs';
import useSWQuery from 'shared-components/hooks/useSWQuery';
import { Method, Service } from 'shared-components/configuration';
*/
const calculateAge = (ssn: string) => {
  const year = Number.parseInt(ssn.slice(0, 4), 10);
  const month = Number.parseInt(ssn.slice(4, 6), 10) - 1; // JS months are 0-based
  const day = Number.parseInt(ssn.slice(6, 8), 10);

  const birthDate = new Date(year, month, day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

const DashboardUser = () => {
  const { name /*accountId*/ } = useMerchantInfo();
  //const [accountInfo, setAccountInfo] = useState<any>({});
  const [stats, setStats] = useState<any>([]);
  const days = 30;

  const columns: TableColumn<any>[] = [
    {
      name: 'Person ID',
      selector: (row) => row.personId || '',
      wrap: true
      //      minWidth: '132px'
    },
    {
      name: 'Volume',
      selector: (row) => `${Number.parseFloat(row.total).toFixed(2)} SEK ` || '',
      wrap: true
      //      minWidth: '132px'
    }
  ];

  const pidHallOfFame = useMemo(() => {
    return (
      stats.map((person: any) => {
        const total = person.volumes.reduce(
          (sum: any, day: any) =>
            sum + day.volumes.reduce((subSum: any, volume: any) => subSum + Number.parseFloat(volume.total), 0),
          0
        );

        const totalCount = person.volumes.reduce(
          (sum: any, day: any) => sum + day.volumes.reduce((subSum: any, volume: any) => subSum + volume.count, 0),
          0
        );

        return {
          personId: person.personId,
          total,
          totalCount,
          age: calculateAge(person.personId)
        };
      }) ?? []
    );
  }, [stats]);

  const volumeChart = useMemo(() => {
    const today = new Date();
    const firstDay = subDays(today, days);

    const result: Record<string, number> = {};

    for (const person of stats) {
      for (const entry of person.volumes) {
        const day = entry.day;
        const totalVolume = entry.volumes.reduce((sum: any, volume: any) => sum + Number.parseFloat(volume.total), 0);
        result[day] = (result[day] || 0) + totalVolume;
      }
    }

    const allDays = eachDayOfInterval({ start: firstDay, end: today }).map((date) => format(date, 'yy-MM-dd'));

    const fullResult = allDays.map((day) => [day, result[day] || 0]);

    return fullResult;
  }, [stats]);

  const last7DaysVolumeChart = useMemo(() => {
    return volumeChart.slice(-7);
  }, [volumeChart]);

  const countChart = useMemo(() => {
    const today = new Date();
    const firstDay = subDays(today, days);

    const result: Record<string, number> = {};

    for (const person of stats) {
      for (const entry of person.volumes) {
        const day = entry.day;
        const totalCount = entry.volumes.reduce((sum: any, count: any) => sum + count.count, 0);
        result[day] = (result[day] || 0) + totalCount;
      }
    }

    const allDays = eachDayOfInterval({ start: firstDay, end: today }).map((date) => format(date, 'yy-MM-dd'));

    const fullResult = allDays.map((day) => [day, result[day] || 0]);

    return fullResult;
  }, [stats, days]);

  const ageGroups = useMemo(() => {
    const groups: Record<string, number> = {
      '0-10': 0,
      '11-20': 0,
      '21-30': 0,
      '31-40': 0,
      '41-50': 0,
      '51-60': 0,
      '61-70': 0,
      '71+': 0
    };

    // Assign each person to the correct group
    for (const { age } of pidHallOfFame) {
      if (age >= 0 && age <= 10) groups['0-10']++;
      else if (age >= 11 && age <= 20) groups['11-20']++;
      else if (age >= 21 && age <= 30) groups['21-30']++;
      else if (age >= 31 && age <= 40) groups['31-40']++;
      else if (age >= 41 && age <= 50) groups['41-50']++;
      else if (age >= 51 && age <= 60) groups['51-60']++;
      else if (age >= 61 && age <= 70) groups['61-70']++;
      else groups['71+']++;
    }

    return Object.entries(groups);
  }, [pidHallOfFame]); // Recalculate only when `pidHallofFame` changes

  const statsQuery = useSWQuery({
    service: Service.GRAPHQL,
    method: Method.QUERY,
    returnObjectName: 'merchantStats',
    data: {
      query: `query merchantStats($currency: String, $cutoff: Int) {
            merchantStats(currency: $currency, cutoff: $cutoff)
      }`,
      variables: {}
    },
    auto: false,
    onResponse: (data: any) => {
      const jsonResult = JSON.parse(data);
      setStats(jsonResult.getPIVolumes);
    }
  });

  const _sebReport = useSWQuery({
    service: Service.GRAPHQL,
    method: Method.QUERY,
    returnObjectName: 'getSebReport',
    data: {
      query: GET_SEB_REPORT,
      variables: {}
    },
    auto: false,
    onResponse: (data: any) => {
      console.log(data);
    }
  });

  const _sebMail = useSWQuery({
    service: Service.GRAPHQL,
    method: Method.QUERY,
    returnObjectName: 'dailySeb',
    data: {
      query: SEB_REPORT_EMAIL,
      variables: {}
    },
    auto: false,
    onResponse: (data: any) => {
      console.log(data);
    }
  });

  useEffect(() => {
    statsQuery.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  /*
  const _accountBalanceQuery = useSWQuery({
    service: Service.GRAPHQL,
    method: Method.QUERY,
    returnObjectName: 'accountBalance',
    data: {
      query: `mutation statementProxy($input: JSONString!) {
            accountBalance: statementProxy(input: $input) {
                result
            }
      }`,
      variables: {}
    },
    auto: false,
    onResponse: (data: any) => {
      const result = data.result;
      const jsonResult = JSON.parse(result);
      setAccountInfo(jsonResult.accountDetails);
    }
  });
  */
  /*
  useEffect(() => {
    _accountBalanceQuery.execute({
      variables: {
        input: JSON.stringify({
          accountDetails: accountId
        })
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);
  */
  return (
    <>
      {/*
      <Button
        onClick={() => {
          sebReport.execute({ variables: { date: '2025-07-08' } });
        }}
      >
        SEB REPORT
      </Button>
      <Button
        onClick={() => {
          sebMail.execute({
            variables: {
              emails: [
                {
                  email: 'mbronowicki.mb@gmail.com',
                  gpgKey:
                    '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'
                }
              ],
              fromDate: '2025-07-10',
              toDate: '2025-07-10'
            }
          });
        }}
      >
        SEB REPORT DIRECT
      </Button>
*/}
      <Row>
        <Col>
          <Card style={{ padding: '10px' }}>
            <Card.Title>
              <center>Account info</center>
            </Card.Title>
            <Card.Body style={{ textAlign: 'left' }}>
              <CorpoStats />
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card style={{ padding: '10px', height: '100%' }}>
            <Card.Title>
              <center>Unique pids</center>
            </Card.Title>
            <Card.Body style={{ textAlign: 'right' }}>{pidHallOfFame.length}</Card.Body>
          </Card>
        </Col>
      </Row>
      <Row style={{ marginTop: '5px' }}>
        <Col>
          <Chart
            chartType="ColumnChart"
            data={[['Date', 'Volume'], ...last7DaysVolumeChart]}
            options={{
              title: 'Last 7 days volume divided by date'
            }}
            legendToggle
          />
        </Col>
      </Row>
      <Row style={{ marginTop: '5px' }}>
        <Col>
          <Chart
            chartType="ColumnChart"
            data={[['Date', 'Volume'], ...volumeChart]}
            options={{
              title: 'Monthly volume divided by date'
            }}
            legendToggle
          />
        </Col>
      </Row>
      <Row style={{ marginTop: '5px' }}>
        <Col>
          <Chart
            chartType="ColumnChart"
            data={[['Date', 'Count'], ...countChart]}
            options={{
              title: 'Monthly transaction count divided by date'
            }}
            legendToggle
          />
        </Col>
      </Row>
      <Row style={{ marginTop: '5px' }}>
        <Col>
          <Chart
            chartType="PieChart"
            data={[['Age', 'Count'], ...ageGroups]}
            options={{
              title: 'Age stats'
            }}
            legendToggle
          />
        </Col>
      </Row>
      <Row style={{ marginTop: '5px' }}>
        <Col>
          <LiveOperations />
        </Col>
      </Row>
      <Row style={{ marginTop: '5px' }}>
        <Col>
          <DataTable
            responsive={true}
            highlightOnHover={true}
            columns={columns}
            pagination
            data={pidHallOfFame ?? []}
            customStyles={{
              headCells: {
                style: {
                  backgroundColor: 'lightgray',
                  textTransform: 'uppercase'
                }
              }
            }}
            className="shadow p-3 mb-5 bg-white overflow-auto"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <LiveOperations />
        </Col>
      </Row>
    </>
  );
};

export default DashboardUser;
